/**
 * Auth store over the pure reducer. The refresh token lives in an HttpOnly
 * cookie and the access token is memory-only; localStorage carries identity
 * only, so every reload resumes with one refresh before re-fetching /me.
 *
 * `runRefresh` is the only refresh path and is single-flight because refresh
 * tokens rotate on every use. A generation counter prevents an older refresh
 * from resurrecting or overwriting a session after logout or a fresh login.
 *
 * Only an auth VERDICT (401) ends the session. A 429, a 5xx or an offline
 * blip is transient and must never sign a working user out -- the server's
 * per-IP refresh budget is shared, so an anonymous flood against it would
 * otherwise log out every user in the building; those failures back off and
 * retry instead.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';

import { ApiError, createApi, HttpClient, type Api, type MeResponse } from '../api/index.ts';
import { authReducer, hasPermission as stateHasPermission, type AuthState } from './reducer.ts';
import { clearAuth, loadAuth, persistAuth, type LoadedAuth } from './storage.ts';

const PROACTIVE_REFRESH_LEAD_MS = 60_000;

/** Back-off ladder for a TRANSIENT refresh failure, then stop trying: the
 *  access token still in memory keeps working until it expires, and the next
 *  real 401 decides the session's fate. */
const REFRESH_RETRY_DELAYS_MS = [5_000, 15_000, 45_000];

/** Did the server actually rule on this session, as opposed to being busy,
 *  broken or unreachable? Only a 401 does. */
function isAuthVerdict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

export interface AuthContextValue {
  state: AuthState;
  api: Api;
  isAuthenticated: boolean;
  hasPermission: (code: string) => boolean;
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  logout: () => Promise<void>;
  forceLogout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** Test seam: override the API client. */
  api?: Api;
}

function sessionFromMe(me: MeResponse, token: string, expiresAt: number) {
  return {
    token,
    email: me.email,
    displayName: me.display_name,
    userId: me.user_id,
    roleName: me.role.name,
    permissions: me.permissions,
    expiresAt,
  };
}

function toInitialAuthState(loaded: LoadedAuth): AuthState {
  if (loaded.status !== 'resumable') return loaded;
  return {
    status: 'authenticated',
    token: null,
    email: loaded.email,
    displayName: loaded.displayName,
    userId: loaded.userId,
    roleName: loaded.roleName,
    permissions: loaded.permissions,
    expiresAt: null,
  };
}

export function AuthProvider({ children, api: apiOverride }: Readonly<AuthProviderProps>): React.JSX.Element {
  const initial = useMemo<LoadedAuth>(() => loadAuth(), []);
  const initialState = useMemo<AuthState>(() => toInitialAuthState(initial), [initial]);
  const [state, dispatch] = useReducer(authReducer, initialState);

  const tokenRef = useRef<string | null>(initialState.token);
  const expiresAtRef = useRef<number | null>(initialState.expiresAt);
  const hasSessionRef = useRef(initialState.status === 'authenticated');
  useEffect(() => {
    tokenRef.current = state.token;
    expiresAtRef.current = state.expiresAt;
    hasSessionRef.current = state.status === 'authenticated';
  }, [state.status, state.token, state.expiresAt]);

  const apiRef = useRef<Api | null>(null);
  const refreshInFlightRef = useRef<Promise<string | null> | null>(null);
  const generationRef = useRef(0);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Self-reference: a scheduled retry re-enters the same single-flight path.
  const runRefreshRef = useRef<(() => Promise<string | null>) | null>(null);

  const clearRetry = useCallback((): void => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const forceLogout = useCallback((): void => {
    generationRef.current += 1;
    tokenRef.current = null;
    expiresAtRef.current = null;
    hasSessionRef.current = false;
    clearRetry();
    retryCountRef.current = 0;
    clearAuth();
    dispatch({ type: 'logout' });
  }, [clearRetry]);

  const scheduleRefreshRetry = useCallback(
    (generation: number): void => {
      const delay = REFRESH_RETRY_DELAYS_MS[retryCountRef.current];
      if (delay === undefined) return; // ladder exhausted; keep the session as-is
      retryCountRef.current += 1;
      clearRetry();
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        if (generationRef.current !== generation || !hasSessionRef.current) return;
        void runRefreshRef.current?.();
      }, delay);
    },
    [clearRetry],
  );

  const runRefresh = useCallback((): Promise<string | null> => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    if (!hasSessionRef.current) return Promise.resolve(null);
    const generation = generationRef.current;

    const attempt = (async (): Promise<string | null> => {
      try {
        const res = await apiRef.current!.auth.refresh();
        if (generationRef.current !== generation) return null;
        const expiresAt = Date.now() + res.expiresInSeconds * 1000;
        tokenRef.current = res.accessToken;
        expiresAtRef.current = expiresAt;
        retryCountRef.current = 0;
        clearRetry();
        dispatch({ type: 'rotateTokens', token: res.accessToken, expiresAt });
        return res.accessToken;
      } catch (err) {
        if (generationRef.current !== generation) return null;
        if (isAuthVerdict(err)) {
          forceLogout();
          return null;
        }
        // Transient (429 / 5xx / offline): the session stands, try again later.
        scheduleRefreshRetry(generation);
        return null;
      }
    })();

    refreshInFlightRef.current = attempt.finally(() => {
      refreshInFlightRef.current = null;
    });
    return refreshInFlightRef.current;
  }, [clearRetry, forceLogout, scheduleRefreshRetry]);

  useEffect(() => {
    runRefreshRef.current = runRefresh;
  }, [runRefresh]);

  // A pending back-off retry must not outlive the provider.
  useEffect(() => clearRetry, [clearRetry]);

  const api = useMemo<Api>(() => {
    if (apiOverride) {
      apiRef.current = apiOverride;
      return apiOverride;
    }
    const http = new HttpClient({ getToken: () => tokenRef.current, onUnauthorized: runRefresh });
    const built = createApi(http);
    apiRef.current = built;
    return built;
  }, [apiOverride, runRefresh]);

  useEffect(() => {
    persistAuth(state);
  }, [state]);

  const login = useCallback(
    async (email: string, password: string, turnstileToken?: string): Promise<void> => {
      generationRef.current += 1;
      // Key omitted entirely when there is no token, so an un-gated login
      // POSTs the exact body it always did.
      const body = turnstileToken === undefined ? { email, password } : { email, password, turnstileToken };
      const res = await api.auth.login(body);
      // Both set BEFORE the /me call: it rides the just-issued token, and a
      // 401 on it may retry-once through runRefresh (cookie already set).
      tokenRef.current = res.accessToken;
      hasSessionRef.current = true;
      try {
        const me = await api.me.get();
        const expiresAt = Date.now() + res.expiresInSeconds * 1000;
        expiresAtRef.current = expiresAt;
        dispatch({ type: 'login', session: sessionFromMe(me, res.accessToken, expiresAt) });
      } catch (err) {
        tokenRef.current = null;
        hasSessionRef.current = false;
        throw err;
      }
    },
    [api],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.auth.logout();
    } catch {
      // Logout is best-effort; the local session must still be cleared.
    }
    forceLogout();
  }, [api, forceLogout]);

  useEffect(() => {
    if (initial.status === 'anonymous') return;
    let cancelled = false;
    (async () => {
      try {
        const fresh = await runRefresh();
        if (cancelled || fresh === null) return;
        const me = await api.me.get();
        if (cancelled || tokenRef.current === null) return;
        dispatch({
          type: 'login',
          session: sessionFromMe(me, tokenRef.current, expiresAtRef.current ?? Date.now()),
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) forceLogout();
      }
    })();
    return () => {
      cancelled = true;
    };
    // Boot-only: later state changes are handled by their dedicated effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.status !== 'authenticated' || state.expiresAt === null) return;
    const delay = state.expiresAt - PROACTIVE_REFRESH_LEAD_MS - Date.now();
    if (delay <= 0) {
      void runRefresh();
      return;
    }
    const timer = setTimeout(() => void runRefresh(), delay);
    return () => clearTimeout(timer);
  }, [state.status, state.expiresAt, runRefresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      api,
      isAuthenticated: state.status === 'authenticated',
      hasPermission: (code: string) => stateHasPermission(state, code),
      login,
      logout,
      forceLogout,
    }),
    [state, api, login, logout, forceLogout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export { anonymousState } from './reducer.ts';
